import React, { PureComponent } from "react"
import classNames from "classnames"
import Link from "next/link"
import { Query } from "react-apollo"

import ProfilePic from "../profile-pic/profile-pic"
import Tag from "../tag/tag"

import { MY_FOUNDER_RECOMMENDATIONS_QUERY } from "../../lib/founder-assist-queries"
import { PollWhile } from "../../lib/founder-assist-jobs"

import "./founder-recommendations.scss"

const DAY_MS = 24 * 60 * 60 * 1000

const TABS = [
  { kind: "founders", label: "Founders" },
  { kind: "ideas", label: "Ideas" },
]

const STRENGTH_LABEL = {
  strong: "Strong match",
  medium: "Good match",
}

function relativeDate(iso) {
  if (!iso) return ""
  const days = Math.max(
    0,
    Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS)
  )
  if (days === 0) return "today"
  if (days === 1) return "yesterday"
  if (days < 14) return `${days} days ago`
  if (days < 56) return `${Math.round(days / 7)} weeks ago`
  return `${Math.round(days / 30)} months ago`
}

function truncate(str, max) {
  if (!str) return ""
  const s = String(str).replace(/<br \/>/g, " ").trim().replace(/\s+/g, " ")
  if (s.length <= max) return s
  return s.slice(0, max - 1).trim() + "…"
}

// Caption shown when there is no LLM reason yet — derived from the card's
// own structured features so the line never reads as empty.
function derivedCaption(industryLabel, stageLabel) {
  const bits = ["Suggested for you"]
  if (industryLabel) bits.push(industryLabel)
  if (stageLabel) bits.push(`${stageLabel} stage`)
  return bits.join(" · ")
}

function friendlyError(error) {
  if (
    error &&
    error.graphQLErrors &&
    error.graphQLErrors.length > 0 &&
    error.graphQLErrors[0].message
  ) {
    return error.graphQLErrors[0].message
  }
  return "Couldn't load recommendations. Please try again."
}

class FounderRecommendations extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      kind: "founders",
      startingRefresh: false,
      refreshError: null,
    }
  }

  selectKind = (kind) => {
    if (kind !== this.state.kind) {
      this.setState({ kind, refreshError: null })
    }
  }

  // refresh: true only *starts* a background recompute. Fire it outside the
  // Query (so polling keeps plain variables and can't trigger more
  // refreshes), then refetch to pick up status "generating" and poll.
  handleRefresh = (client, refetch) => {
    const { kind } = this.state
    this.setState({ startingRefresh: true, refreshError: null })
    client
      .query({
        query: MY_FOUNDER_RECOMMENDATIONS_QUERY,
        variables: { kind, refresh: true },
        fetchPolicy: "no-cache",
      })
      .then(() => refetch())
      .catch((e) => this.setState({ refreshError: friendlyError(e) }))
      .then(() => this.setState({ startingRefresh: false }))
  }

  renderReason(pick, caption) {
    if (pick.reason) {
      return (
        <p className="founder-recommendations__reason">
          {pick.matchStrength && STRENGTH_LABEL[pick.matchStrength] && (
            <span
              className={classNames(
                "founder-recommendations__strength",
                `founder-recommendations__strength--${pick.matchStrength}`
              )}
            >
              {STRENGTH_LABEL[pick.matchStrength]}
            </span>
          )}
          <span className="founder-recommendations__reason-text">
            {pick.reason}
          </span>
        </p>
      )
    }
    return (
      <p className="founder-recommendations__reason founder-recommendations__reason--derived">
        {caption}
      </p>
    )
  }

  renderFounderCard(pick) {
    const founder = pick.founder
    if (!founder) return null
    const metaBits = []
    if (founder.location || founder.locationCountry) {
      metaBits.push(founder.location || founder.locationCountry)
    }
    if (founder.lastActive) {
      metaBits.push(`Active ${relativeDate(founder.lastActive)}`)
    }
    const excerpt = truncate(
      founder.elevatorPitchForStartup || founder.whyMakeGoodCofounder,
      120
    )
    return (
      <article
        key={pick.refId}
        className="founder-recommendations__card"
        role="listitem"
      >
        <div className="founder-recommendations__card-main">
          {founder.profilePicMed && (
            <ProfilePic
              href={"/profile/[id]"}
              hrefAs={`/profile/${founder.id}`}
              profilePic={founder.profilePicMed}
              firstName={founder.firstName}
              width={48}
              height={48}
            />
          )}
          <div className="founder-recommendations__card-heading">
            <Link href="/profile/[id]" as={`/profile/${founder.id}`}>
              <a className="founder-recommendations__card-name">
                {founder.firstName}
              </a>
            </Link>
            {metaBits.length > 0 && (
              <div className="founder-recommendations__card-meta">
                {metaBits.join(" · ")}
              </div>
            )}
          </div>
        </div>
        {this.renderReason(
          pick,
          derivedCaption(founder.industryLabel, founder.stageLabel)
        )}
        {excerpt && (
          <p className="founder-recommendations__excerpt">{excerpt}</p>
        )}
        {founder.skills && founder.skills.length > 0 && (
          <div className="founder-recommendations__tags">
            {founder.skills.slice(0, 4).map((skill) => (
              <Tag key={skill} label={skill} title={skill} />
            ))}
          </div>
        )}
      </article>
    )
  }

  renderIdeaCard(pick) {
    const idea = pick.idea
    if (!idea) return null
    const engagementBits = []
    if (idea.likeCount > 0) {
      engagementBits.push(
        `${idea.likeCount} like${idea.likeCount === 1 ? "" : "s"}`
      )
    }
    if (idea.saveCount > 0) {
      engagementBits.push(
        `${idea.saveCount} save${idea.saveCount === 1 ? "" : "s"}`
      )
    }
    return (
      <article
        key={pick.refId}
        className="founder-recommendations__card"
        role="listitem"
      >
        <div className="founder-recommendations__card-heading">
          <Link href="/ideas/[id]" as={`/ideas/${idea.id}`}>
            <a className="founder-recommendations__card-name">{idea.title}</a>
          </Link>
          {idea.createdByFirstName && (
            <div className="founder-recommendations__card-meta">
              by {idea.createdByFirstName}
            </div>
          )}
        </div>
        {this.renderReason(
          pick,
          derivedCaption(idea.industryLabel, idea.stageLabel)
        )}
        {idea.elevatorPitch && (
          <p className="founder-recommendations__excerpt">
            {truncate(idea.elevatorPitch, 140)}
          </p>
        )}
        {engagementBits.length > 0 && (
          <div className="founder-recommendations__engagement">
            {engagementBits.join(" · ")}
          </div>
        )}
      </article>
    )
  }

  render() {
    // Free members only get founder picks (no ideas tab, no refresh)
    const isPremium = this.props.isPremium !== false
    const kind = isPremium ? this.state.kind : "founders"

    return (
      <Query
        query={MY_FOUNDER_RECOMMENDATIONS_QUERY}
        variables={{ kind }}
        fetchPolicy="cache-and-network"
        notifyOnNetworkStatusChange
        ssr={false}
      >
        {({
          data,
          loading,
          error,
          refetch,
          client,
          startPolling,
          stopPolling,
        }) => {
          const recs = data && data.myFounderRecommendations
          const picks = (recs && recs.picks) || []
          const refreshesLeft = recs ? recs.refreshesLeftToday : null
          // Matches are computed in the background; poll until ready.
          const generating = Boolean(recs && recs.status === "generating")
          const refreshing =
            (loading && Boolean(recs)) ||
            generating ||
            this.state.startingRefresh
          const shownError =
            this.state.refreshError || (error ? friendlyError(error) : null)

          let body
          if (loading && !recs) {
            body = (
              <div className="founder-recommendations__loading">
                Loading recommendations…
              </div>
            )
          } else if (generating && picks.length === 0) {
            body = (
              <div className="founder-recommendations__loading">
                Finding your matches…
              </div>
            )
          } else if (error && !recs) {
            body = (
              <p className="founder-recommendations__error">
                {friendlyError(error)}
              </p>
            )
          } else if (picks.length === 0) {
            body = (
              <div className="founder-recommendations__empty">
                <p>Complete your profile to get matches</p>
              </div>
            )
          } else {
            body = (
              <div className="founder-recommendations__cards" role="list">
                {picks.map((pick) =>
                  kind === "ideas"
                    ? this.renderIdeaCard(pick)
                    : this.renderFounderCard(pick)
                )}
              </div>
            )
          }

          return (
            <div className="founder-recommendations">
              <PollWhile
                active={generating && !error}
                startPolling={startPolling}
                stopPolling={stopPolling}
              />
              <header className="founder-recommendations__header">
                <div className="founder-recommendations__header-text">
                  <h2 className="founder-recommendations__eyebrow">
                    Suggested connections
                  </h2>
                  {isPremium && (
                  <div className="founder-recommendations__tabs" role="tablist">
                    {TABS.map((tab) => (
                      <button
                        key={tab.kind}
                        type="button"
                        role="tab"
                        aria-selected={kind === tab.kind}
                        className={classNames("founder-recommendations__tab", {
                          "founder-recommendations__tab--active":
                            kind === tab.kind,
                        })}
                        onClick={() => this.selectKind(tab.kind)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  )}
                </div>
                {isPremium ? (
                <div className="founder-recommendations__refresh-wrap">
                  <button
                    type="button"
                    className="founder-recommendations__refresh"
                    onClick={() => this.handleRefresh(client, refetch)}
                    disabled={!recs || refreshing || refreshesLeft === 0}
                  >
                    {refreshing ? "Refreshing…" : "Refresh"}
                  </button>
                  {refreshesLeft != null && (
                    <span className="founder-recommendations__refresh-note">
                      {refreshesLeft} refresh
                      {refreshesLeft === 1 ? "" : "es"} left today
                    </span>
                  )}
                </div>
                ) : (
                  // Free: one founder a week (backend utils/founder-entitlements.js)
                  <p className="founder-recommendations__free-note">
                    One new founder each week on Free.{" "}
                    <Link href="/pricing">
                      <a>Premium suggests founders and ideas anytime</a>
                    </Link>
                  </p>
                )}
              </header>

              {shownError && recs && (
                <p className="founder-recommendations__error">{shownError}</p>
              )}

              {body}

              {recs && recs.computedAt && !generating && (
                <div className="founder-recommendations__footer">
                  Updated {relativeDate(recs.computedAt)}
                  {recs.llmUsed ? " · Curated with AI" : ""}
                </div>
              )}
            </div>
          )
        }}
      </Query>
    )
  }
}

export default FounderRecommendations
